import { Head, Link } from "@inertiajs/react"
import { ChevronRight, MapPinned, Plus } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type DestinationRow = {
  id: number
  name_en: string | null
  name_de: string | null
  kind: string
  status: string
  parent_name: string | null
  updated_at: string | null
}

export default function AdminDestinationsIndex({ destinations }: { destinations: DestinationRow[] }) {
  return (
    <>
      <Head title="Destinations">
        <meta name="description" content="Manage destinations." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Destinations</h1>
              <p className="mt-1">{destinations.length} destinations in the field guide.</p>
            </div>
            <Button asChild>
              <Link href="/admin/destinations/new">
                <Plus className="h-4 w-4" />
                New destination
              </Link>
            </Button>
          </div>
        </div>

        <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
          {destinations.map((destination) => (
            <li key={destination.id}>
              <Link
                href={`/admin/destinations/${destination.id}`}
                className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
              >
                <MapPinned className="h-4 w-4 text-ink-muted" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink-display">
                    {destination.name_en ?? destination.name_de ?? `Destination #${destination.id}`}
                  </div>
                  <div className="truncate text-xs text-ink-muted">
                    {titleize(destination.kind)}
                    {destination.parent_name ? ` in ${destination.parent_name}` : ""}
                  </div>
                </div>
                <Badge tone={destination.status === "published" ? "accent" : "muted"}>
                  {titleize(destination.status)}
                </Badge>
                <ChevronRight className="h-4 w-4 text-ink-muted" />
              </Link>
            </li>
          ))}
        </ul>
      </AdminShell>
    </>
  )
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}
